import { FC } from 'react'
import {
  FundStage,
  getFundStageDisplayLabel,
  isComingSoonStage,
  isOpenPreIpoFundStage,
  isWhitelistedStage,
} from '@domain/funds/model'

interface PreIpoStageTagProps {
  stage: string | null | undefined
  size?: 'md' | 'lg' | 'icon'
}

export const PreIpoStageTag: FC<PreIpoStageTagProps> = ({ stage, size = 'md' }) => {
  if (!stage) return null

  const label = getFundStageDisplayLabel(stage)
  const normalized = stage.toLowerCase()
  const isIcon = size === 'icon'
  const isWhitelist = isWhitelistedStage(stage)
  const isGreen =
    (isOpenPreIpoFundStage(stage) && !isWhitelist) ||
    normalized === FundStage.Active.toLowerCase() ||
    normalized === FundStage.Vesting.toLowerCase()
  const isUpcoming = normalized === FundStage.Upcoming.toLowerCase()
  const isComingSoon = isComingSoonStage(stage)
  const isPaused = normalized === FundStage.Paused.toLowerCase()
  const isInactive = normalized === FundStage.Inactive.toLowerCase()
  const isProposed = normalized === FundStage.Proposed.toLowerCase()

  const sizeClass = isIcon
    ? 'h-4 w-4 justify-center p-0 shrink-0'
    : size === 'lg'
      ? 'px-3 py-1 text-sm gap-2'
      : 'px-2 py-0.5 text-xs gap-1.5'
  const dotClass = isIcon || size === 'md' ? 'w-1.5 h-1.5' : 'w-2 h-2'
  const base = `flex items-center rounded-md font-semibold uppercase tracking-wide leading-tight ${sizeClass}`

  let tone = 'bg-cw-purple-light text-cw-electric-purple border border-cw-electric-purple'
  let dotTone = 'bg-cw-electric-purple'
  let displayLabel = 'Available'

  if (isWhitelist) {
    tone = 'bg-cw-grey-light text-cw-grey-dark border border-cw-grey-dark'
    dotTone = 'bg-cw-grey-dark'
    displayLabel = label
  } else if (isGreen) {
    tone = 'bg-cw-green-teal-light text-cw-green-dark border border-cw-green-dark'
    dotTone = 'bg-cw-green-dark'
    displayLabel = label
  } else if (isUpcoming) {
    tone = 'bg-cw-purple-light text-cw-midnight-purple border border-cw-midnight-purple'
    dotTone = 'bg-cw-midnight-purple'
    displayLabel = label
  } else if (isComingSoon) {
    tone = 'bg-cw-grey-light text-cw-grey-dark border border-cw-grey-dark'
    dotTone = 'bg-cw-grey-dark'
    displayLabel = 'Coming Soon'
  } else if (isPaused) {
    tone = 'bg-cw-grey-light text-cw-grey-dark border border-cw-grey-dark'
    dotTone = 'bg-cw-grey-dark'
    displayLabel = 'Paused'
  } else if (isInactive) {
    tone = 'bg-cw-bright-grey text-cw-midnight-purple/60'
    dotTone = 'bg-cw-midnight-purple/40'
    displayLabel = label
  } else if (isProposed) {
    tone = 'bg-cw-orange-light text-cw-orange-dark border border-cw-orange-dark'
    dotTone = 'bg-cw-orange-dark'
    displayLabel = label
  }

  return (
    <div className={`${base} ${tone}`} aria-label={displayLabel}>
      <span className={`inline-block ${dotClass} ${dotTone} rounded-full align-middle`} />
      {!isIcon && displayLabel}
    </div>
  )
}
